import type React from 'react'
import { useContext } from 'react'
import { Link } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { SurveyLinkContext } from '../../SurveyLinkContext'

const SURVEY_TAG_REGEX = /<email>(.*?)<\/email>|<link\s+url="(.*?)">(.*?)<\/link>|<tab\s+id="(\d+)">(.*?)<\/tab>|<priority\s+num="(\d+)"(?:\s+activity="([^"]*)")?>(.*?)<\/priority>/g

interface TabLinkProps {
  tabId: string
  children: React.ReactNode
}

const TabLink = ({ tabId, children }: TabLinkProps): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault()
    // URL shape: /(Planning|Implementation)/:rcnId/:planId/:tab[/...]
    // Truncate everything after :tab and replace :tab with the requested id.
    const segments = location.pathname.split('/')
    const baseIndex = segments.findIndex(s => s === 'Planning' || s === 'Implementation')
    if (baseIndex === -1) return
    const tabIndex = baseIndex + 3
    const truncated = segments.slice(0, tabIndex + 1)
    truncated[tabIndex] = tabId
    navigate(truncated.join('/'))
  }

  return (
    <Link href="#" onClick={handleClick}>{children}</Link>
  )
}

interface PriorityLinkProps {
  priorityNum: number
  activity?: string
  children: React.ReactNode
}

const PriorityLink = ({ priorityNum, activity, children }: PriorityLinkProps): JSX.Element => {
  const { rcnId, planId, planOrgId, priorityTaskIds } = useContext(SurveyLinkContext)
  const taskId = priorityTaskIds[priorityNum]
  const canNavigate = rcnId != null && planId != null && planOrgId != null && taskId != null

  if (!canNavigate) return <>{children}</>

  const basePath = `/Implementation/${String(rcnId)}/${String(planId)}/3/PlanOrganization/${String(planOrgId)}/Priority/${String(taskId)}`
  const query = activity != null && activity !== '' ? `?activity=${encodeURIComponent(activity)}` : ''

  return (
    <Link href={basePath + query} target="_blank" rel="noopener noreferrer">{children}</Link>
  )
}

export function parseSurveyText (text: string): React.ReactNode {
  if (text == null || text === '') return text

  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0

  while ((match = SURVEY_TAG_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    if (match[1] != null) {
      // <email>addr</email>
      parts.push(
        <Link key={key++} href={`mailto:${match[1]}`}>{match[1]}</Link>
      )
    } else if (match[2] != null && match[3] != null) {
      // <link url="...">text</link>
      parts.push(
        <Link key={key++} href={match[2]} target="_blank" rel="noopener noreferrer">{match[3]}</Link>
      )
    } else if (match[4] != null && match[5] != null) {
      // <tab id="N">text</tab> — replaces the last segment of the current URL with N
      parts.push(
        <TabLink key={key++} tabId={match[4]}>{match[5]}</TabLink>
      )
    } else if (match[6] != null && match[8] != null) {
      // <priority num="N" activity="..."?>text</priority> — navigates to this plan org's priority N page
      parts.push(
        <PriorityLink key={key++} priorityNum={Number(match[6])} activity={match[7]}>{match[8]}</PriorityLink>
      )
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex === 0) return text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return <>{parts}</>
}
